'use client';

import { useEffect, useState } from 'react';
import type { IFlashCampaign, IFlashSale } from '@/sanity/lib/definitions';
import { getLiveFlashCampaign, topLevelCampaign } from '@/lib/flashSale';

/**
 * Client-side live check for the Flash Sale singleton: which campaign (the
 * flash pair or one of the seasonal entries) is live right now.
 *
 * The pages are ISR-cached, so a server-side Date.now() would be frozen into
 * the HTML — instead the state starts as `null` (matching whatever the cached
 * markup shows, avoiding hydration mismatches) and is re-evaluated on an
 * interval, exactly like hooks/useCampaignLive.tsx does for the single-window
 * widgets. An open tab therefore switches shortly after each boundary.
 *
 * QA/preview override, inert in normal operation:
 *   ?flashPreview=1   the flash pair (while it is enabled)
 *   ?flashPreview=0   the standard state, whatever is live
 *   ?flashPreview=c1  the first seasonal entry, c2 the second, ... (or an
 *                     entry's _key) — while that entry is enabled
 */

/**
 * How often the schedule is re-evaluated.
 *
 * Campaigns are scheduled to the minute, so a one-minute cadence matches the
 * granularity of the data. Note the interval is not aligned to the wall clock:
 * it starts when the page loads, so the switch lands up to this long after the
 * boundary. That only affects tabs left open across the boundary — a fresh
 * load checks immediately, and so does returning to a hidden tab.
 *
 * A visible countdown reaching zero would need a tighter value.
 */
const CHECK_INTERVAL_MS = 60_000;

export const FLASH_PREVIEW_PARAM = 'flashPreview';

/** undefined = no override; null = force the standard state. */
function resolvePreview(
  config: IFlashSale,
  value: string | null,
): IFlashCampaign | null | undefined {
  if (value === null || value === '') return undefined;
  if (value === '0') return null;
  if (value === '1') return config.isEnabled ? topLevelCampaign(config) : null;
  const list = config.campaigns ?? [];
  const index = /^c\d+$/i.test(value) ? Number(value.slice(1)) - 1 : -1;
  const entry = list[index] ?? list.find((campaign) => campaign._key === value);
  if (!entry) return undefined;
  return entry.isEnabled ? entry : null;
}

export function useLiveFlashCampaign(
  config: IFlashSale | null | undefined,
): IFlashCampaign | null {
  const [campaign, setCampaign] = useState<IFlashCampaign | null>(null);

  useEffect(() => {
    if (!config) {
      setCampaign(null);
      return;
    }

    const preview = resolvePreview(
      config,
      new URLSearchParams(window.location.search).get(FLASH_PREVIEW_PARAM),
    );
    if (preview !== undefined) {
      setCampaign(preview);
      return;
    }

    // The first evaluation of a config always lands (the config may have
    // changed under the same keys); later ticks keep the same object while
    // the live campaign's key is unchanged, so the consumers do not re-render
    // every minute (the flash pair is rebuilt by topLevelCampaign each time).
    let settled = false;
    const check = () => {
      const next = getLiveFlashCampaign(config);
      setCampaign((previous) =>
        settled && next?._key === previous?._key ? previous : next,
      );
      settled = true;
    };
    check();

    // Background tabs are throttled and sleeping devices stop timers
    // altogether, so re-check as soon as the page becomes visible again
    // instead of waiting out the interval.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisibility);

    const interval = setInterval(check, CHECK_INTERVAL_MS);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [config]);

  return campaign;
}

/** True while any campaign of the singleton is live. */
export function useFlashSaleLive(config: IFlashSale | null | undefined): boolean {
  return useLiveFlashCampaign(config) !== null;
}
